/**
 * Drill Report Screen
 * Collects drill report data with offline support
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Save, WifiOff, Loader2 } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router'

import { ActionsTimeline } from '@/components/DrillTimer/ActionsTimeline'
import { ResponderSelector } from '@/components/DrillTimer/ResponderSelector'
import { ResponderSignatures } from '@/components/DrillTimer/ResponderSignatures'
import { SignatureCapture } from '@/components/DrillTimer/SignatureCapture'
import { VideoUpload } from '@/components/DrillTimer/VideoUpload'
import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import { Textarea } from '@/components/ui/textarea'
import { useOnlineStatus } from '@/hooks/useOffline'
import { drillTimerApi } from '@/services/drillTimerService'
import { getUsers } from '@/services/user.service'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

export function DrillReportScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { entityId } = useParams()
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()

  // Get drill data from navigation state
  const {
    drillTimer,
    locationOfDrill,
    proctor,
    records = [],
    startTime,
    endTime,
  } = location.state || {}

  const [formData, setFormData] = useState({
    notes: '',
    responders: [],
    signature: null,
    video: null,
  })

  const [responderSignatures, setResponderSignatures] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch available users for responder selection
  const { data: usersData, isLoading: loadingUsers } = useQuery({
    queryKey: ['users', entityId],
    queryFn: () =>
      getUsers({
        sorting: [],
        pagination: { pageIndex: 0, pageSize: 1000 },
        searchTerm: '',
        filters: { entity: entityId ? [entityId] : [] },
      }),
    staleTime: 5 * 60 * 1000,
    enabled: !!entityId,
  })

  const availableUsers = usersData?.results || []

  // Redirect if no drill data
  useEffect(() => {
    if (!drillTimer || !records.length) {
      navigate('../setup')
    }
  }, [drillTimer, records, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate signature
    if (!formData.signature) {
      toast('Please provide a signature to complete the report', 'error')
      return
    }

    setIsSubmitting(true)

    // Build report data
    const templateRoles = drillTimer.roles || []
    const reportData = {
      name: `${drillTimer.name}_${new Date(startTime).toISOString()}`,
      drill_name: drillTimer.name,
      location_of_drill: locationOfDrill,
      roles: templateRoles.length > 0 ? templateRoles : ['Responder'],
      is_rescue_ready: drillTimer.isRescueReady || drillTimer.is_rescue_ready || false,
      start_time: startTime,
      end_time: endTime,
      proctor: proctor?.id
        ? {
            user_id: proctor.id.toString(),
            user_name: formatUserName(proctor),
            roles: proctor.roles || [],
            signature: formData.signature,
          }
        : {
            user_id: '',
            user_name: '',
            roles: [],
            signature: formData.signature,
          },
      responders: formData.responders.map((responder) => ({
        ...responder,
        signature: responderSignatures[responder.user_id] || null,
      })),
      records,
      notes: formData.notes,
      lessons_learned: formData.lessonsLearned || '',
      video: formData.video,
    }

    try {
      const isCurrentlyOnline = navigator.onLine

      if (!isCurrentlyOnline) {
        // Save offline with entityId for later sync
        const { db } = await import('@/utils/db')
        await db.pendingReports.add({
          reportData,
          entityId,
          queuedAt: new Date(),
          status: 'pending',
          retryCount: 0,
        })

        toast('Report saved offline. Will sync when connection is restored.', {
          duration: 5000,
          icon: '📴',
        })

        queryClient.invalidateQueries({ queryKey: ['drillLogs', entityId] })
        navigate('..', {
          state: {
            message: 'Report saved and will sync when connection is restored',
            type: 'info',
          },
        })
      } else {
        // Submit online - entityId is now in the URL path
        await drillTimerApi.createLog(entityId, reportData)

        // Handle video upload if present
        if (formData.video) {
          try {
            await drillTimerApi.uploadVideo(entityId, formData.video, drillTimer.name)
          } catch (videoError) {
            // Video upload failed but main report was successful, continue
          }
        }

        toast('Report submitted successfully!')
        queryClient.invalidateQueries({ queryKey: ['drillLogs', entityId] })
        navigate('..', {
          state: { message: 'Drill report submitted successfully' },
        })
      }
    } catch (error) {
      toast('Failed to submit drill report. Please try again.', 'error')
      setIsSubmitting(false)
    }
  }

  const calculateDuration = () => {
    if (!startTime || !endTime) return '0:00'
    const diff = new Date(endTime) - new Date(startTime)
    const minutes = Math.floor(diff / 60000)
    const seconds = Math.floor((diff % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  if (!drillTimer) {
    return (
      <Layout
        breadcrumbs={[
          { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
          'Complete Report',
        ]}
      >
        <div className="mx-auto max-w-4xl space-y-6 p-6">
          {/* Drill summary, responders, and signature card placeholders */}
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout
      breadcrumbs={[
        { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
        'Complete Report',
      ]}
    >
      <Layout.Heading title="Drill Report" subtitle={`${drillTimer.name} - ${locationOfDrill}`} />

      {/* Online Status Indicator */}
      {!isOnline && (
        <div className="border-b bg-white px-6 py-2">
          <div className="mx-auto flex max-w-4xl items-center gap-2 text-sm">
            <WifiOff className="h-4 w-4 text-orange-600" />
            <span className="text-orange-600">
              Offline - Report will be saved and synced when connection is restored
            </span>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="mx-auto max-w-4xl p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Drill Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Drill Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Template:</span>
                  <p className="font-medium">{drillTimer.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Location:</span>
                  <p className="font-medium">{locationOfDrill}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Proctor:</span>
                  <p className="font-medium">{proctor ? formatUserName(proctor) : 'N/A'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <p className="font-medium">{calculateDuration()}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Actions Logged:</span>
                  <p className="font-medium">{records.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Responders */}
          {loadingUsers ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Loader2 className="text-muted-foreground mx-auto h-6 w-6 animate-spin" />
                <p className="text-muted-foreground mt-2 text-sm">Loading users...</p>
              </CardContent>
            </Card>
          ) : (
            <ResponderSelector
              availableUsers={availableUsers}
              availableRoles={drillTimer.roles || []}
              value={formData.responders}
              onRespondersChange={(responders) => setFormData((prev) => ({ ...prev, responders }))}
            />
          )}

          {/* Responder Signatures */}
          {formData.responders.length > 0 && (
            <ResponderSignatures
              responders={formData.responders}
              onSignaturesChange={setResponderSignatures}
            />
          )}

          {/* Actions Timeline */}
          <ActionsTimeline records={records} startTime={startTime} />

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Add any notes or observations about the drill..."
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </CardContent>
          </Card>

          {/* Video Upload */}
          <VideoUpload
            value={formData.video}
            onVideoSelect={(video) => setFormData((prev) => ({ ...prev, video }))}
          />

          {/* Signature */}
          <SignatureCapture
            required
            onSignatureComplete={(signature) => setFormData((prev) => ({ ...prev, signature }))}
          />

          {/* Submit Button */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('..')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.signature}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isOnline ? 'Submitting...' : 'Saving Offline...'}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isOnline ? 'Submit Report' : 'Save Offline & Sync Later'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  )
}
